import {
  IArraySorter,
  IDestinationMigrationJobState,
} from '@principle-theorem/principle-core/interfaces';
import {
  PrimaryKey,
  WithPrimaryKey,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { type IDestinationEntity } from './destination-entity';
import {
  DestinationEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntityRecord,
  type MergeConflictDestinationEntityRecord,
  type MissingCustomMappingDestinationEntityRecord,
  type SkippedDestinationEntityRecord,
} from './destination-entity-record';
import {
  type IDestinationJobFilter,
  type IDestinationJobFilterHandler,
} from './destination-job-filter';
import { type IPracticeMigration } from './practice-migration';
import { type ISourceEntityHandler } from './source';
import { ISourceEntityRecord } from './source-entity-record';
import { type ITranslationMapHandler } from './translation-mappings';

export interface IDestinationEntityJobRunOptions {
  runType: IDestinationMigrationJobState['type'];
}

export interface IDestinationEntityHandler<
  SuccessRecordData extends object = object,
  InitialJobData extends object = object,
  BuiltMigrationData extends object = object,
> {
  destinationEntity: IDestinationEntity;
  sourceCountComparison: ISourceEntityHandler;
  sourceEntities?: Record<symbol, ISourceEntityHandler>;
  destinationEntities?: Record<symbol, IDestinationEntityHandler>;
  filters: IDestinationJobFilterHandler<InitialJobData>[];
  sorters?: IArraySorter[];
  batchLimit: number;
  concurrency: number;
  canMigrateByDateRange: boolean;
  canMigrateByIdRange: boolean;
  canHandle(destination: IDestinationEntity): boolean;
  getDestinationEntityRecordUid(data: InitialJobData): string;
  sourceCountDataAccessor(
    data: InitialJobData
  ): PrimaryKey<ISourceEntityRecord>;
  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: ITranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<InitialJobData[]>;
  buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: ITranslationMapHandler,
    data: InitialJobData
  ):
    | Promise<
        | BuiltMigrationData
        | (IDestinationEntityRecord & FailedDestinationEntityRecord)
        | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
        | (IDestinationEntityRecord &
            MissingCustomMappingDestinationEntityRecord)
      >
    | (
        | BuiltMigrationData
        | (IDestinationEntityRecord & FailedDestinationEntityRecord)
        | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
        | (IDestinationEntityRecord &
            MissingCustomMappingDestinationEntityRecord)
      );
  shouldSkipRecord(
    jobData: InitialJobData,
    filters: IDestinationJobFilter[]
  ): boolean;
  hasMergeConflict(
    translationMap: ITranslationMapHandler,
    data: BuiltMigrationData
  ): Promise<BuiltMigrationData | undefined> | (BuiltMigrationData | undefined);
  /**
   * Implemented by entities whose migration merges onto the existing document,
   * so the merge conflict diff shows what migrating would save rather than the
   * migration data. The diff can't be edited, because edits to the migration
   * data are merged too.
   */
  buildMergeConflictDiff?(
    translationMap: ITranslationMapHandler,
    data: BuiltMigrationData
  ): Promise<IMergeConflictDiff<BuiltMigrationData> | undefined>;
  buildMergeConflictRecord(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: ITranslationMapHandler,
    jobData: InitialJobData,
    migrationData: BuiltMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord;
  runJob(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: ITranslationMapHandler,
    jobData: InitialJobData,
    migrationData: BuiltMigrationData
  ): Promise<IDestinationEntityRecord>;
  getEntity$(
    migration: IReffable<IPracticeMigration>
  ): Observable<WithRef<IDestinationEntity> | undefined>;
  getRecords$(
    migration: IReffable<IPracticeMigration>,
    bufferSize: number,
    initialRecord?: WithPrimaryKey<IDestinationEntityRecord>,
    statuses?: DestinationEntityRecordStatus[]
  ): Observable<WithPrimaryKey<IDestinationEntityRecord>[]>;
  getMigratedData$?(
    record: IDestinationEntityRecord<SuccessRecordData>
  ): Observable<IMigratedDataSummary[]>;
  rollbackRecord?(
    record: IDestinationEntityRecord<SuccessRecordData>
  ): Promise<void>;
  buildSkippedResponse(
    data: InitialJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord;
}

export interface IMigrateErrorData {
  resumeData: object;
  errorMessage: string;
}

export interface IMigratedDataSummary {
  label: string;
  data: object;
}

/** Principle's current data against the data migrating would save. */
export interface IMergeConflictDiff<MigrationData extends object = object> {
  principle: MigrationData;
  migration: MigrationData;
}
